import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';
import { adminAPI } from '../../../../services/api';
import { useAdminToast } from '../../../../hooks/useAdminToast';
import { ModalWrapper } from './ModalComponents';

const MassAssignModal = ({ selectedClients, managers, currentUser, statuses, availableTags, onClose, onSuccess }) => {
  const { toast } = useAdminToast();
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [customTag, setCustomTag] = useState('');
  const [useCustomTag, setUseCustomTag] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Determine if user is super admin
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const getRoleLabel = (role) => {
    const labels = { admin: 'Админ', manager: 'Менеджер', super_admin: 'Супер Админ' };
    return labels[role] || role;
  };
  
  // Get the actual tag to use (custom or selected)
  const getTagToApply = () => {
    if (useCustomTag && customTag.trim()) {
      return customTag.trim();
    }
    return selectedTag || null;
  };

  const handleSubmit = async () => {
    const tagToApply = getTagToApply();
    if (!selectedManager && !selectedStatus && !tagToApply) {
      toast.validation('Выберите хотя бы одно действие');
      return;
    }
    setLoading(true);
    try {
      // If manager is selected, use unified mass-assign endpoint with all options
      if (selectedManager) {
        await adminAPI.massAssignClients(
          selectedClients, 
          selectedManager, 
          selectedStatus || null, 
          tagToApply
        );
      } else {
        // If no manager, handle status and tag separately
        if (selectedStatus) {
          await adminAPI.massStatusChange(selectedClients, selectedStatus);
        }
        if (tagToApply) {
          await adminAPI.massTagChange(selectedClients, tagToApply, 'add');
        }
      }
      
      const actions = [];
      if (selectedManager) actions.push('назначены');
      if (selectedStatus) actions.push('статус изменён');
      if (tagToApply) actions.push('тег добавлен');
      
      toast.success(`Успешно: ${actions.join(', ')} для ${selectedClients.length} клиентов`);
      onSuccess();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter: Admins should NOT see other admins (only managers)
  // Super Admins see everyone
  const filteredByRole = isSuperAdmin 
    ? managers 
    : managers.filter(m => m.role !== 'admin' || m.id === currentUser?.id);

  // Filter by search query
  const filteredStaff = filteredByRole.filter(m => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      (m.name || '').toLowerCase().includes(searchLower) ||
      (m.email || '').toLowerCase().includes(searchLower)
    );
  });
  
  // Check if there's any selection for submit button
  const tagToApplyForCheck = useCustomTag ? customTag.trim() : selectedTag;
  const hasSelection = selectedManager || selectedStatus || tagToApplyForCheck;

  // Sort: admins first, then managers
  const sortedStaff = [...filteredStaff].sort((a, b) => {
    if (a.role === 'admin' && b.role !== 'admin') return -1;
    if (a.role !== 'admin' && b.role === 'admin') return 1;
    return (a.name || a.email || '').localeCompare(b.name || b.email || '');
  });

  return (
    <ModalWrapper title="Массовое назначение и изменение" onClose={onClose}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ padding: '16px', backgroundColor: COLORS.background, borderRadius: '8px', marginBottom: '16px' }}>
          <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '4px' }}>Выбрано клиентов</div>
          <div style={{ color: COLORS.primary, fontSize: '28px', fontWeight: '700' }}>{selectedClients.length}</div>
        </div>

        {/* Manager Selection */}
        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: `${COLORS.primary}08`, borderRadius: '8px', border: `1px solid ${COLORS.primary}20` }}>
          <label style={{ display: 'block', color: COLORS.text, fontSize: '13px', marginBottom: '8px', fontWeight: '600' }}>
            👤 Назначить на сотрудника
          </label>
          
          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по имени или email..."
              style={{ 
                width: '100%', 
                padding: '10px 10px 10px 36px', 
                backgroundColor: COLORS.card, 
                border: `1px solid ${COLORS.border}`, 
                borderRadius: '6px', 
                color: COLORS.text, 
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <select 
            value={selectedManager} 
            onChange={(e) => setSelectedManager(e.target.value)} 
            style={{ width: '100%', padding: '10px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '6px', color: COLORS.text, fontSize: '13px' }}
          >
            <option value="">— Не менять назначение —</option>
            {sortedStaff.map(m => (
              <option key={m.id} value={m.id}>
                {m.name || m.email} — {getRoleLabel(m.role)}
              </option>
            ))}
          </select>
        </div>

        {/* Status Selection */}
        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: `#8b5cf608`, borderRadius: '8px', border: `1px solid #8b5cf620` }}>
          <label style={{ display: 'block', color: COLORS.text, fontSize: '13px', marginBottom: '8px', fontWeight: '600' }}>
            📊 Изменить статус
          </label>
          <select 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)} 
            style={{ width: '100%', padding: '10px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '6px', color: COLORS.text, fontSize: '13px' }}
          >
            <option value="">— Не менять статус —</option>
            {(statuses || []).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* Tag Selection */}
        <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: `#14b8a608`, borderRadius: '8px', border: `1px solid #14b8a620` }}>
          <label style={{ display: 'block', color: COLORS.text, fontSize: '13px', marginBottom: '8px', fontWeight: '600' }}>
            🏷️ Добавить тег
          </label>
          
          {/* Toggle: Custom or Select */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            <button
              onClick={() => { setUseCustomTag(false); setCustomTag(''); }}
              style={{
                flex: 1,
                padding: '8px',
                backgroundColor: !useCustomTag ? COLORS.primary : COLORS.card,
                border: `1px solid ${!useCustomTag ? COLORS.primary : COLORS.border}`,
                borderRadius: '6px',
                color: !useCustomTag ? '#000' : COLORS.textSecondary,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Выбрать из списка
            </button>
            <button
              onClick={() => { setUseCustomTag(true); setSelectedTag(''); }}
              style={{
                flex: 1,
                padding: '8px',
                backgroundColor: useCustomTag ? COLORS.primary : COLORS.card,
                border: `1px solid ${useCustomTag ? COLORS.primary : COLORS.border}`,
                borderRadius: '6px',
                color: useCustomTag ? '#000' : COLORS.textSecondary,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Кастомный тег
            </button>
          </div>
          
          {!useCustomTag ? (
            <select 
              value={selectedTag} 
              onChange={(e) => setSelectedTag(e.target.value)} 
              style={{ width: '100%', padding: '10px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '6px', color: COLORS.text, fontSize: '13px' }}
            >
              <option value="">— Не добавлять тег —</option>
              {(availableTags || []).map(t => (
                <option key={typeof t === 'object' ? t.name : t} value={typeof t === 'object' ? t.name : t}>
                  {typeof t === 'object' ? t.name : t}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              placeholder="Введите название тега..."
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                color: COLORS.text,
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          )}
        </div>

        {/* Summary */}
        {hasSelection && (
          <div style={{ padding: '12px', backgroundColor: `${COLORS.success}10`, borderRadius: '8px', border: `1px solid ${COLORS.success}30` }}>
            <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Будет применено:</div>
            <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
              {selectedManager && <div>✓ Назначение на: {sortedStaff.find(m => m.id === selectedManager)?.name || sortedStaff.find(m => m.id === selectedManager)?.email}</div>}
              {selectedStatus && <div>✓ Статус: {selectedStatus}</div>}
              {(useCustomTag ? customTag.trim() : selectedTag) && <div>✓ Тег: {useCustomTag ? customTag.trim() : selectedTag}</div>}
            </div>
          </div>
        )}
      </div>

      <button 
        onClick={handleSubmit} 
        disabled={loading || !hasSelection} 
        style={{ 
          width: '100%', 
          padding: '14px', 
          backgroundColor: loading || !hasSelection ? COLORS.border : COLORS.primary, 
          border: 'none', 
          borderRadius: '8px', 
          color: loading || !hasSelection ? COLORS.textSecondary : '#000', 
          fontSize: '15px', 
          fontWeight: '600', 
          cursor: loading || !hasSelection ? 'not-allowed' : 'pointer' 
        }}
      >
        {loading ? 'Применение...' : `Применить к ${selectedClients.length} клиентам`}
      </button>
    </ModalWrapper>
  );
};

export default MassAssignModal;
